// Guards navigation away from an AI agent view (PAICE, Talk to My Data,
// Map My Data, Graph My Data) while it is streaming a response.
//
// The streaming view registers itself while a response is in flight; the
// router's global beforeEach and the sidebar's CID switch call
// confirmLeaveAgentStream() before navigating. Kept as module state rather than
// Vuex because it holds a component reference and an abort callback.

let activeStream = null; // { owner, agentName, abort }
let pendingConfirm = null;

const onBeforeUnload = (event) => {
    event.preventDefault();
    event.returnValue = ''; // required by Chrome to show the native prompt
};

// Call when a view starts streaming. `abort` (optional) stops the stream when
// the user confirms leaving; omit it when the request can't be cancelled.
export function registerAgentStream(owner, agentName, abort) {
    if (!activeStream) {
        window.addEventListener('beforeunload', onBeforeUnload);
    }
    activeStream = { owner, agentName, abort };
}

// Call when the view stops streaming or unmounts. Only the registering view
// can release, so a stale owner can't clear another view's stream.
export function releaseAgentStream(owner) {
    if (activeStream && activeStream.owner === owner) {
        activeStream = null;
        window.removeEventListener('beforeunload', onBeforeUnload);
    }
}

// Resolves true when navigation may proceed: nothing is streaming, or the user
// confirmed (in which case the stream is aborted and released).
export function confirmLeaveAgentStream(store) {
    if (!activeStream) return Promise.resolve(true);
    // A second navigation while the prompt is open (e.g. another sidebar
    // click) shares the same answer instead of stacking modals.
    if (pendingConfirm) return pendingConfirm;

    const { agentName, abort } = activeStream;
    pendingConfirm = new Promise((resolve) => {
        store.dispatch('showModal', {
            component: 'agentStreamLeave',
            data: {
                agentName,
                canAbort: typeof abort === 'function',
                screensize: 'md',
                confirm() {
                    const stream = activeStream;
                    if (stream) {
                        try {
                            if (typeof stream.abort === 'function') stream.abort();
                        } catch (error) {
                            console.error('Failed to abort agent stream:', error);
                        }
                        releaseAgentStream(stream.owner);
                    }
                    resolve(true);
                },
                cancel() {
                    resolve(false);
                },
            },
        });
        // Dismissing via the X, backdrop or Escape bypasses cancel(); treat it
        // as "Stay". A no-op once confirm() has already resolved.
        document.getElementById('modalBasic')
            ?.addEventListener('hidden.bs.modal', () => resolve(false), { once: true });
    }).finally(() => {
        pendingConfirm = null;
    });
    return pendingConfirm;
}
